import React from 'react';
import './SnapGuides.css';

const SnapGuides = ({
  isVisible = false,
  snapLines = [],
  containerWidth = 0,
  containerHeight = 0,
  snapThreshold = 10
}) => {
  if (!isVisible || snapLines.length === 0) {
    return null;
  }

  return (
    <div className="snap-guides-overlay">
      {snapLines.map((line, index) => (
        <div
          key={index}
          className={`snap-line ${line.type}`}
          style={{
            position: 'absolute',
            ...(line.type === 'vertical' ? {
              left: `${line.position}px`,
              top: 0,
              width: '1px',
              height: '100%',
              borderLeft: '1px dashed #00b5ad'
            } : {
              top: `${line.position}px`,
              left: 0,
              height: '1px',
              width: '100%',
              borderTop: '1px dashed #00b5ad'
            })
          }}
        />
      ))}
    </div>
  );
};

/**
 * Calculate snap positions for an item being moved
 * @param {Object} movingItem - The item being moved
 * @param {Array} otherItems - Other items on the canvas
 * @param {Object} container - Container dimensions
 * @param {number} threshold - Snap threshold in pixels
 * @returns {Object} Snap information
 */
export const calculateSnapPositions = (movingItem, otherItems, container, threshold = 10) => {
  const snapLines = [];
  const snapPositions = { x: null, y: null };
  
  if (!movingItem || !container) {
    return { snapLines, snapPositions };
  }

  const movingRect = {
    left: movingItem.x,
    right: movingItem.x + movingItem.width,
    top: movingItem.y,
    bottom: movingItem.y + movingItem.height,
    centerX: movingItem.x + movingItem.width / 2,
    centerY: movingItem.y + movingItem.height / 2
  };

  // Container center lines
  const containerCenterX = container.width / 2;
  const containerCenterY = container.height / 2;

  // Check snap to container center
  if (Math.abs(movingRect.centerX - containerCenterX) < threshold) {
    snapLines.push({ type: 'vertical', position: containerCenterX });
    snapPositions.x = containerCenterX - movingItem.width / 2;
  }

  if (Math.abs(movingRect.centerY - containerCenterY) < threshold) {
    snapLines.push({ type: 'horizontal', position: containerCenterY });
    snapPositions.y = containerCenterY - movingItem.height / 2;
  }

  // Check snap to other items
  otherItems.forEach(item => {
    if (item.id === movingItem.id) return;

    const itemRect = {
      left: item.x,
      right: item.x + item.width,
      top: item.y,
      bottom: item.y + item.height,
      centerX: item.x + item.width / 2,
      centerY: item.y + item.height / 2
    };

    // Vertical alignment checks
    // Left edges
    if (Math.abs(movingRect.left - itemRect.left) < threshold) {
      snapLines.push({ type: 'vertical', position: itemRect.left });
      snapPositions.x = itemRect.left;
    }
    // Right edges
    if (Math.abs(movingRect.right - itemRect.right) < threshold) {
      snapLines.push({ type: 'vertical', position: itemRect.right });
      snapPositions.x = itemRect.right - movingItem.width;
    }
    // Centers
    if (Math.abs(movingRect.centerX - itemRect.centerX) < threshold) {
      snapLines.push({ type: 'vertical', position: itemRect.centerX });
      snapPositions.x = itemRect.centerX - movingItem.width / 2;
    }
    // Left to right edge
    if (Math.abs(movingRect.left - itemRect.right) < threshold) {
      snapLines.push({ type: 'vertical', position: itemRect.right });
      snapPositions.x = itemRect.right;
    }
    // Right to left edge
    if (Math.abs(movingRect.right - itemRect.left) < threshold) {
      snapLines.push({ type: 'vertical', position: itemRect.left });
      snapPositions.x = itemRect.left - movingItem.width;
    }

    // Horizontal alignment checks
    // Top edges
    if (Math.abs(movingRect.top - itemRect.top) < threshold) {
      snapLines.push({ type: 'horizontal', position: itemRect.top });
      snapPositions.y = itemRect.top;
    }
    // Bottom edges
    if (Math.abs(movingRect.bottom - itemRect.bottom) < threshold) {
      snapLines.push({ type: 'horizontal', position: itemRect.bottom });
      snapPositions.y = itemRect.bottom - movingItem.height;
    }
    // Centers
    if (Math.abs(movingRect.centerY - itemRect.centerY) < threshold) {
      snapLines.push({ type: 'horizontal', position: itemRect.centerY });
      snapPositions.y = itemRect.centerY - movingItem.height / 2;
    }
    // Top to bottom edge
    if (Math.abs(movingRect.top - itemRect.bottom) < threshold) {
      snapLines.push({ type: 'horizontal', position: itemRect.bottom });
      snapPositions.y = itemRect.bottom;
    }
    // Bottom to top edge
    if (Math.abs(movingRect.bottom - itemRect.top) < threshold) {
      snapLines.push({ type: 'horizontal', position: itemRect.top });
      snapPositions.y = itemRect.top - movingItem.height;
    }
  });

  return { snapLines, snapPositions };
};

/**
 * Apply snap positions to item coordinates
 * @param {Object} item - Item to snap
 * @param {Object} snapPositions - Calculated snap positions
 * @returns {Object} Updated item coordinates
 */
export const applySnapPositions = (item, snapPositions) => {
  return {
    ...item,
    x: snapPositions.x !== null ? snapPositions.x : item.x,
    y: snapPositions.y !== null ? snapPositions.y : item.y
  };
};

export default SnapGuides;